<template>
	<div v-if="category && categoryData" class="budget-cycle-page">
		<ja-header :title="category.name" :show-home="true" :show-intro="true">
			<template v-slot:icon>
				<svgicon :name="`expenseCategories/${category.assetName}`"></svgicon>
			</template>
		</ja-header>
		<content @scroll="onContentScroll($event)">
			<div class="category-section">
				<ja-research :data="categoryData" :category="category"></ja-research>
			</div>
			<h2 v-if="!showShoppingCart" class="heading-3">
				{{
					$t('onSiteSimulationShoppingSubtitle', {
						minimum: lowestExpenseSubCategoryCost,
						maximum: highestExpenseSubCategoryCost,
						minimumPercentage: minUpperBudgetPercentage,
						maximumPercentage: maxUpperBudgetPercentage
					})
				}}
			</h2>
			<div v-if="showShoppingCart" class="category-section">
				<ja-shopping
					:category="category"
					:data="categoryData"
					:preselected-data="preselectedItems"
					:preview-only="haveToBudget"
					:cart="cartData"
					:area="area"
					:payment-methods="cmsUpperPaymentMethods"
					:discard-all-purchases="onDiscardAllPurchases"
					:deselected-data-id="deselectedID"
					@cartUpdated="onCartUpdated"
					@autoLoanInterest="autoLoanInterestSelection"
				>
				</ja-shopping>
			</div>
			<div class="category-section">
				<div v-if="haveToBudget" class="slider-container">
					<h5
						v-if="isLoansTextVisible"
						class="text--center text--warning-dimmed mb--2"
					>
						{{
							$t(`applyForLoanNote.${requiredOrOptionalLoan}`, {
								loanType: $t(`${loanType}`)
							})
						}}
					</h5>
					<ja-currency-slider
						:remaining-budget="remainingBudget + budgeted"
						:initial-value="budgetFromPrevousStage"
						:recommended-range="[
							calculateGuideLine(minUpperBudgetPercentage),
							calculateGuideLine(maxUpperBudgetPercentage)
						]"
						:range="[0, initialLeftToBudget]"
						@valueChanged="budgeted = $event"
					></ja-currency-slider>
				</div>
				<ja-payment
					v-else
					:cart-data="cartData"
					:all-subcategories="categoryData"
					:required-categories="requiredCategories"
					:total-cost="totalCost"
					:have-to-use-loan="haveToUseLoan"
					:loan-type="loanType"
					:category-budget="categoryBudget"
					:preselected-payment-method="preselectedPaymentMethod"
					:preselected-items="preselectedItemsIds"
					:skipped-purchase-id="skippedPurchaseId"
					:category="category"
					:auto-loan-interest-selection="autoLoanInterest"
					@alreadyCompleted="refetchDataAndRedirectoTohome"
					@paymentLoading="paymentLoading = $event"
					@paymentCompleted="onPaymentCompleted"
					@removeItemFromCart="removeItemFromCart"
					@discardAllPurchases="showDiscardConfirmation"
				></ja-payment>
			</div>
		</content>
		<footer class="sticky-footer">
			<div class="left-btn-wrapper">
				<div v-if="haveToBudget" class="remaining-budget pt--3 pb--3">
					<div>
						<span class="text--muted text--uppercase text--block text--md">
							{{ $t('leftToBudget') }}
						</span>
						<span
							class="text--block text--md text--bold-500 mt--1"
							:class="{ 'text--warning': remainingBudget <= 0 }"
						>
							{{ remainingBudget | currency }}
						</span>
					</div>
				</div>
				<div v-else class="range-wrapper">
					<div class="range-labels">
						<h4 :class="{ overflow: totalCost > categoryBudget }">
							{{ totalCost | currency }}
						</h4>
						<div class="body-text-2">
							{{ categoryBudget | currency }}
						</div>
					</div>
					<ja-budget-bar
						aria-hidden="true"
						:value="totalCost"
						:max-value="categoryBudget"
						:overflow="totalCost > categoryBudget"
					>
					</ja-budget-bar>
				</div>
			</div>
			<div v-if="haveToBudget">
				<ja-button
					class="small"
					name="finish-shopping"
					:aria-label="$t('finishShopping', { category: category.name })"
					:aria-disabled="!budgeted"
					:disabled="!budgeted || budgeted > initialLeftToBudget"
					@click.once.native="updateCategoryBudget"
				>
					<span class="button-text">{{ $t('finish') }}</span>
				</ja-button>
			</div>
			<div v-else>
				<ja-button
					v-if="showSkipShoppingButton"
					class="small outlined"
					name="skip-shopping"
					@click.once.native="checkAndUpdate"
				>
					<span class="button-text">{{ $t('skipShopping') }}</span>
				</ja-button>
				<ja-button
					class="small"
					name="finish-shopping"
					:aria-label="$t('finishShopping', { category: category.name })"
					:aria-disabled="!paymentCompleted"
					:disabled="!paymentCompleted"
					@click.once.native="finishShopping"
				>
					<span class="button-text">{{ $t('finish') }}</span>
				</ja-button>
			</div>
			<ja-chat-bot
				v-if="userData.userActiveSimulationAIChatbotEnable"
				:is-chat-in-footer="true"
				:hide-chatbot-button="$store.state.loading"
			/>
		</footer>
		<ja-confirm-modal
			:show="isDiscardConfirmationVisible"
			@closeModal="closeDiscardModal"
			@cancel="closeDiscardModal"
			@confirm="discardAllPurchases"
		>
			{{ $t('discardPurchasesMessage') }}
		</ja-confirm-modal>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import UserData from '@/mixins/UserData';
import ProfileData from '@/mixins/ProfileData';
import LifeStageData from '@/mixins/LifeStageData';
import Savings from '@/mixins/Savings';
import Debts from '@/mixins/Debts';
import Header from '../components/ui/headers/Header';
import Button from '../components/ui/Button';
import BudgetBar from '../components/ui/BudgetBar';
import CurrencySlider from '@/components/LifeStageIntro/elements/CurrencySlider';
import Research from '../components/BudgetCycle/Research';
import Shopping from '../components/BudgetCycle/Shopping';
import Payment from '../components/BudgetCycle/Payment';
import StudentsService from '@/services/StudentsService';
import ExpenseCategoryService from '@/services/ExpenseCategoryService';
import graphqlClient from '@/helpers/graphqlClient';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import FacilitiesService from '@/services/FacilitiesService';
import ChatBot from '@/components/ChatBot/ChatBot';

import {
	applicationLoanTypes,
	loanApplicableCategories,
	maritalStatus
} from '@/helpers/constants';
export default {
	components: {
		jaHeader: Header,
		jaButton: Button,
		jaResearch: Research,
		jaShopping: Shopping,
		jaPayment: Payment,
		jaBudgetBar: BudgetBar,
		jaCurrencySlider: CurrencySlider,
		jaConfirmModal: ConfirmModal,
		jaChatBot: ChatBot
	},
	mixins: [UserData, LifeStageData, ProfileData, Savings, Debts],
	props: {
		category: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			categoryData: null,
			cartData: [],
			paymentCompleted: false,
			skipShoppingIsEnabled: false,
			paymentLoading: false,
			budgeted: 0,
			totalDebtsAndSavingsAmount: 0,
			budgetFromPrevousStage: 0,
			initialLeftToBudget: 0,
			preselectedPaymentMethod: null,
			preselectedItems: [],
			preselectedItemsIds: [],
			isDiscardConfirmationVisible: false,
			skippedPurchaseId: 0,
			minUpperBudgetPercentage: 0,
			maxUpperBudgetPercentage: 0,
			highestExpenseSubCategoryCost: 0,
			lowestExpenseSubCategoryCost: 0,
			deletionIds: [],
			subCategories: [],
			cmsUpperPaymentMethods: [],
			autoLoanInterest: null,
			area: null,
			onDiscardAllPurchases: false,
			deselectedID: null
		};
	},
	apollo: {
		cmsUpperPaymentMethods: {
			query: gql`
				{
					cmsUpperPaymentMethods {
						name
						defaultName
						upperPaymentMethod_ID
					}
				}
			`,
			update(data) {
				return data.cmsUpperPaymentMethods;
			}
		}
	},
	computed: {
		billboard() {
			if (this.category.expenseCategory_ID) {
				return this.$store.getters.billboardByCategory(
					this.category.expenseCategory_ID
				);
			}
			return null;
		},
		haveToBudget() {
			if (this.category) {
				// isInBudgetingPhase comes from the lifeStage mixin
				return this.isInBudgetingPhase;
			}
			return false;
		},
		showShoppingCart() {
			// Hide shopping card in budgeting phase
			if (this.category) {
				return !this.isInBudgetingPhase;
			}
			return true;
		},
		totalCost() {
			return this.cartData.reduce((sum, item) => {
				if (item.totalCost >= 0) {
					return sum + parseInt(item.totalCost);
				}
			}, 0);
		},
		categoryBudget() {
			if (this.category) {
				return this.category.studentExpenseCategoryBudgets.reduce(function(
					sum,
					item
				) {
					return sum + item.budget;
				},
				0);
			}
			return 0;
		},
		isLoansTextVisible() {
			return this.isAnyLoanVisible && this.haveToUseLoan && this.haveToBudget;
		},
		loanType() {
			// Use assetName as "unique" part for a category.
			// Same property is used to get Loan Categories in category service
			if (
				this.category &&
				this.category.assetName === loanApplicableCategories.TRANSPORTATION
			) {
				return applicationLoanTypes.AUTO_LOAN;
			}

			// it is not needed to define another if statement,
			// because if category is not one of those two,
			// this won't be visible anyway. (the html container depends on isLoansTextVisible)
			return applicationLoanTypes.MORTGAGE_LOAN;
		},
		requiredOrOptionalLoan() {
			// Get proper text translation
			return this.$store.getters.isLoanRequired ? 'required' : 'optional';
		},
		requiredCategories() {
			const requiredCategories = [];

			// If category is not mandatory we don't want to
			// check for subcateogires - just ignore the option
			// same applies for shopping component.
			if (
				this.categoryData.hasOwnProperty('isMandatoryForCurrentStudent') &&
				!this.categoryData.isMandatoryForCurrentStudent
			) {
				return requiredCategories;
			}

			this.categoryData.cmsExpenseSubCategories.forEach(element => {
				if (element.isMandatory) {
					requiredCategories.push(element.expenseSubCategory_ID);
				}
			});

			return requiredCategories;
		},
		remainingBudget() {
			// initialLeftToBudget won't be changed until Finish is pressed,
			// so becaus it is in computed property this will trigger multiple other
			// properties and force the same calcuclation over and over which is not needed
			return this.initialLeftToBudget - this.budgeted;
		},
		showSkipShoppingButton() {
			return (
				!this.paymentCompleted &&
				this.category &&
				!this.category.isMandatoryForCurrentStudent &&
				this.skipShoppingIsEnabled &&
				!this.paymentLoading
			);
		},
		haveToUseLoan() {
			const allowedCategories = ['transportation', 'housing'];

			return (
				this.category && allowedCategories.includes(this.category.assetName)
			);
		}
	},
	async mounted() {
		this.area = await FacilitiesService.getUniqueAreas();
		if (this.category) {
			if (this.category.studentExpenseCategoryBudgets[0]) {
				// use || because in some cases budget is null instead of any number
				this.budgetFromPrevousStage =
					this.category.studentExpenseCategoryBudgets[0].budget || 0;
			}

			// Set this.budgeted in order to be used in computed properties
			// with correct values
			this.budgeted = this.budgetFromPrevousStage;
			// Call getLeftToBudget (ProfileData method) only once - here
			// that way the calcuclations will be triggered only once
			// No need to recalc something many times with the same result
			// argument is needed in order to subtract the current value
			// from categories' total
			this.initialLeftToBudget = this.getLeftToBudget(this.budgeted);

			this.$route.meta.title = this.category.name;
		}
	},
	created() {
		if (this.category) {
			this.loadCategoryData();
		} else {
			this.$store.commit('setLoading', true);
			this.$router.push({ path: '/home' });
		}
	},
	beforeRouteLeave(to, from, next) {
		if (window.popStateDetected) {
			graphqlClient.stop();
			this.$store.commit('setLoading', true);
			this.$store.commit('resetLifeStageData');
			this.$store.commit('resetCategories');
			window.popStateDetected = false;
		}
		next();
	},
	methods: {
		autoLoanInterestSelection(selection) {
			this.autoLoanInterest = selection;
		},
		onContentScroll(e) {
			const target = e.target;

			if (target.scrollHeight - target.scrollTop - target.clientHeight <= 1) {
				this.skipShoppingIsEnabled = true;
			}
		},
		onCartUpdated(payload) {
			this.cartData = [...payload];
		},
		onPaymentCompleted() {
			this.paymentCompleted = true;
		},
		// Check if category is already skipped or
		// there are subcategories already purchased
		async checkAndUpdate() {
			// TODO: Refactor this.Use external function to avoid code repetition
			const subCategoriesIds = this.categoryData.cmsExpenseSubCategories.map(
				s => s.expenseSubCategory_ID
			);

			const { skippedCategory } = await StudentsService.checkBeforeUpdate(
				this.userData.studentId,
				this.lifeStageData.lifeStage_ID,
				subCategoriesIds,
				this.category.expenseCategory_ID,
				true
			);

			// If Ids are the same go to home. Category is already skipped
			// If not - then there is probably another opened tab and the Id is different
			// This can produce a specific edge case where Skipped is selected but
			// Puchased items are presented in next opening.
			if (skippedCategory.id === this.skippedPurchaseId) {
				this.refetchDataAndRedirectoTohome();
				return;
			}

			this.skipShopping();
		},
		skipShopping() {
			this.completeExpenseCategoryWithoutPaying().then(response => {
				if (response) {
					this.refetchDataAndRedirectoTohome();
				}
			});
		},
		finishShopping() {
			if (this.paymentCompleted) {
				this.refetchDataAndRedirectoTohome();
			}
		},
		refetchDataAndRedirectoTohome() {
			this.$store.commit('setLoading', true);
			this.$store.commit('resetLifeStageData');
			this.$store.commit('resetCategories');
			this.goToHomePage();
		},
		goToHomePage() {
			this.$router.push({ path: '/home' });
		},
		async completeExpenseCategoryWithoutPaying() {
			if (this.userData && this.lifeStageData) {
				let studentExpenseCategory = {
					expenseCategory_ID: this.category.expenseCategory_ID,
					studentId: this.userData.studentId,
					lifeStage_ID: this.lifeStageData.lifeStage_ID,
					noPurchaseIsMade: true,
					isUnlockedWithPasscode: true
				};

				return await ExpenseCategoryService.addOrUpdateStudentCategory(
					studentExpenseCategory,
					this.skippedPurchaseId,
					this.preselectedItemsIds
				);
			}
		},
		async loadCategoryData() {
			this.categoryData = await ExpenseCategoryService.getCategoryData(
				this.category.expenseCategory_ID,
				this.userData
			);
			const subcategories = this.categoryData.cmsExpenseSubCategories.map(
				i => i.expenseSubCategory_ID
			);
			if (
				this.categoryData.isCompletedForCurrentStudent &&
				!this.categoryData.noPurchaseIsMade
			) {
				const purchases = await ExpenseCategoryService.getPurchasesForCategory({
					studentId: this.userData.studentId,
					lifeStageId: this.userData.studentLifeStageId,
					categoryId: this.categoryData.expenseCategory_ID,
					subCategoriesIds: subcategories
				});

				// this.preselectedItems = purchases.subcategories.map(
				// 	i => i.expenseSubCategoryId
				// );
				const payments = purchases.subcategories
					.filter(
						i =>
							i.paymentMethod == 1 ||
							i.paymentMethod == 2 ||
							i.paymentMethod == 3 ||
							i.paymentMethod == 4 ||
							i.paymentMethod == 5 ||
							i.paymentMethod == null
					)
					.map(i => i.paymentMethod);
				this.preselectedPaymentMethod = payments.length ? payments[0] : null;
				this.preselectedItems = purchases.subcategories;
				this.preselectedItemsIds = purchases.subcategories.map(i => i.id);

				this.skippedPurchaseId = purchases.skippedPurchase.id || 0;
			}

			if (this.categoryData) {
				this.highestExpenseSubCategoryCost = this.categoryData.highestExpenseSubCategoryCost;
				this.lowestExpenseSubCategoryCost = this.categoryData.lowestExpenseSubCategoryCost;
			}

			if (this.categoryData.cmsUpperBudgetGuidelines.length > 0) {
				this.minUpperBudgetPercentage = this.categoryData.cmsUpperBudgetGuidelines[0].minimum;
				this.maxUpperBudgetPercentage = this.categoryData.cmsUpperBudgetGuidelines[0].maximum;
			}

			this.$store.commit('setLoading', false);
		},
		async updateCategoryBudget() {
			// TODO: Should add check before update here
			// Two-tabs validation - if an ID already exists
			// Add it to the object to prevent issues
			const alreadyBudgeted = this.category.studentExpenseCategoryBudgets[0];

			const updObject = {
				budget: this.budgeted,
				expenseCategory_Id: this.category.expenseCategory_ID,
				studentId: this.userData.studentId,
				lifeStage_ID: this.userData.studentLifeStageId,
				isAutomaticallyCreated: false
			};

			if (alreadyBudgeted) {
				updObject.id = alreadyBudgeted.id;
			}

			const { data } = await ExpenseCategoryService.updateCategoryBudget(
				updObject
			);

			if (data) {
				this.refetchDataAndRedirectoTohome();
			}
		},
		async removeItemFromCart(id) {
			const isFoundItem = this.preselectedItems.some(
				item => item.expenseSubCategoryId === id
			);
			if (
				isFoundItem &&
				(this.cartData.length === 2 || this.preselectedItems.length === 2) &&
				(this.profileData.cmsMaritalStatus.status.toLowerCase() ===
					this.$t('married') ||
					this.profileData.cmsMaritalStatus.status.toLowerCase() ===
						maritalStatus.MARRIED.toLowerCase()) &&
				this.category.assetName.toLowerCase() ===
					loanApplicableCategories.TRANSPORTATION
			) {
				this.cartData = [];
				this.onDiscardAllPurchases = true;
				this.$store.commit('resetLifeStageData');

				await ExpenseCategoryService.makePurchase(
					[],
					this.preselectedItemsIds,
					this.skippedPurchaseId
				);
				this.preselectedItemsIds = [];
				this.preselectedItems = [];
				this.onDiscardAllPurchases = false;
			} else if (!isFoundItem || this.preselectedItems.length === 0) {
				this.cartData = this.cartData.filter(item => {
					return item.expenseSubCategory_ID !== id;
				});
				const foundItem = this.preselectedItems.find(
					item => item.expenseSubCategoryId === id
				);

				const removedItemId = foundItem ? [foundItem.id] : [];

				this.deselectedID = { expenseSubCategory_ID: id };

				await ExpenseCategoryService.makePurchase(
					[],
					removedItemId,
					this.skippedPurchaseId
				);
			} else {
				this.cartData = this.cartData.filter(item => {
					return item.expenseSubCategory_ID !== id;
				});

				const foundItem = this.preselectedItems.find(
					item => item.expenseSubCategoryId === id
				);

				const removedItemId = foundItem ? [foundItem.id] : [];

				this.deselectedID = { expenseSubCategory_ID: id };

				this.$store.commit('resetLifeStageData');

				await ExpenseCategoryService.makePurchase(
					[],
					removedItemId,
					this.skippedPurchaseId
				);

				this.preselectedItems = this.preselectedItems.filter(
					data => data.id != foundItem.id
				);
				this.preselectedItemsIds = this.preselectedItemsIds.filter(
					id => id != foundItem.id
				);
			}
		},
		showDiscardConfirmation() {
			this.isDiscardConfirmationVisible = true;
		},
		closeDiscardModal() {
			this.isDiscardConfirmationVisible = false;
		},
		async discardAllPurchases() {
			if (this.preselectedItems.length === 0) {
				this.cartData = [];
				this.isDiscardConfirmationVisible = false;
				this.onDiscardAllPurchases = true;
				await ExpenseCategoryService.makePurchase(
					[],
					this.preselectedItemsIds,
					this.skippedPurchaseId
				);
				this.onDiscardAllPurchases = false;
			} else {
				this.cartData = [];
				this.isDiscardConfirmationVisible = false;
				this.onDiscardAllPurchases = true;
				this.$store.commit('resetLifeStageData');
				await ExpenseCategoryService.makePurchase(
					[],
					this.preselectedItemsIds,
					this.skippedPurchaseId
				);
				this.preselectedItemsIds = [];
				this.preselectedItems = [];
				this.onDiscardAllPurchases = false;
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.budget-cycle-page {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;

	header {
		flex-shrink: 0;
	}

	content {
		display: flex;
		flex-direction: column;
		align-items: center;
		flex-grow: 1;
		overflow: auto;
		position: relative;

		.category-section {
			width: 100%;
			padding-bottom: 64px;

			&:last-child {
				padding-top: 64px;
				border-top: 1px solid $container-border;
			}
		}

		.slider-container {
			width: 75%;
			max-width: 768px;
			margin: 0 auto;
		}
	}

	footer {
		width: 100%;
		height: 76px;
		padding: 0 5%;
		display: flex;
		position: relative;
		z-index: 1;
		flex: 0 0 auto;
		align-items: center;
		justify-content: flex-end;
		background-color: $container-bgcolor;
		box-shadow: 0px 0px 4px -1px rgba(0, 0, 0, 0.45);
		flex-shrink: 0;
		width: 100%;
		display: flex;
		align-items: center;
		font-family: 'RobotoMedium';

		.outlined {
			margin-right: 16px;
		}

		.left-btn-wrapper {
			display: flex;
			flex-grow: 1;

			.range-wrapper {
				width: 30%;
				height: 100%;

				.range-labels {
					display: flex;
					align-items: center;

					h4 {
						flex-grow: 1;
						margin: 0;
						color: $font-blue;

						&.overflow {
							color: $budget-bar-overflow-color;
						}
					}

					.body-text-2 {
						color: $input-color;
					}
				}

				.budget-bar {
					::v-deep.filled:not(.overflow) {
						background-color: $primary-state;
					}
				}
			}
		}

		.navigation-btn-text {
			position: relative;
			top: 1px;
			font-family: 'RobotoMedium';
		}
	}
}
</style>
